/**
 * What the client needs to know about a market, and nothing more.
 *
 * The server's `app/market.py` is the source of truth — session times, holiday
 * keys, data paths, benchmarks. None of that belongs here. This is only the
 * presentation half: what to call a market, and how to format a number that
 * came from one.
 *
 * ## The market is derived from the symbol, never stored
 *
 * `RELIANCE.NS` carries its own market (D33), so a row, a chart or a watchlist
 * entry needs no extra field to know which one it is in. That is why there is no
 * "current market" anywhere in the workspace: a global mode can fall out of step
 * with what is on screen, and a suffix cannot.
 *
 * ## Format per row, never sort across rows
 *
 * D40's rule for every surface that mixes markets. Formatting is safe because a
 * row knows its own market — ₹ and lakh resolve individually. Sorting is not:
 * `marketCap` is in native currency, so a descending sort across markets puts
 * every Indian mid-cap above every US megacap. Where an ordering is wanted it is
 * rank *within* a market, which `/sym/symbols` already returns.
 */

import { primarySymbol } from "./composite";

export interface MarketInfo {
  /** ISO country code, matching the server's `Market.code`. */
  readonly code: string;
  /** What to show a user. */
  readonly label: string;
  /** Suffix a bare ticker carries in this market. Empty for the default. */
  readonly suffix: string;
  /** BCP 47 — `en-IN` is what groups by lakh and crore rather than K/M/B. */
  readonly locale: string;
  /** ISO 4217, for `Intl.NumberFormat`'s currency mode. */
  readonly currency: string;
}

export const US: MarketInfo = {
  code: "US",
  label: "US",
  suffix: "",
  locale: "en-US",
  currency: "USD",
};

export const IN: MarketInfo = {
  code: "IN",
  label: "NSE",
  suffix: ".NS",
  locale: "en-IN",
  currency: "INR",
};

export const MARKETS: readonly MarketInfo[] = [US, IN];

/**
 * The market a symbol belongs to, from its suffix alone.
 *
 * A **composite** resolves on its first operand: `RELIANCE.NS/NIFTYBEES.NS` is
 * an Indian ratio, and reading the whole string would find no `.NS` at the end
 * of it and format the row in dollars. The first leg is the one whose currency
 * and volume the composite already takes, so it is the one that decides here.
 *
 * No resolver is passed, and it is not needed: `/`, `*` and `+` decide on sight,
 * and a lone hyphen falls back to "one ticker" — which is right for
 * `BAJAJ-AUTO.NS`, and harmless for a genuine spread, whose legs share a market
 * in every case anyone would chart.
 */
export function marketForSymbol(symbol: string | null | undefined): MarketInfo {
  const upper = primarySymbol((symbol ?? "").toUpperCase());
  // Longest suffix first, so a longer one can never be shadowed by a shorter
  // one that happens to be its tail. The empty US suffix matches everything and
  // is the fallback rather than a candidate.
  const withSuffix = MARKETS.filter((m) => m.suffix).sort(
    (a, b) => b.suffix.length - a.suffix.length,
  );
  return withSuffix.find((m) => upper.endsWith(m.suffix)) ?? US;
}

/** A market by code, defaulting rather than throwing — this is presentation. */
export function marketByCode(code: string | null | undefined): MarketInfo {
  if (!code) return US;
  return MARKETS.find((m) => m.code === code.toUpperCase()) ?? US;
}

/**
 * The ticker without its market suffix, for showing beside an explicit badge.
 *
 * `RELIANCE.NS` reads as `RELIANCE` under an `NSE` badge; repeating the suffix
 * next to the badge says the same thing twice in less space.
 */
export function bareSymbol(symbol: string): string {
  const market = marketForSymbol(symbol);
  return market.suffix && symbol.toUpperCase().endsWith(market.suffix)
    ? symbol.slice(0, -market.suffix.length)
    : symbol;
}

const compactFormatters = new Map<string, Intl.NumberFormat>();
const priceFormatters = new Map<string, Intl.NumberFormat>();

/**
 * A market cap in its own market's conventions.
 *
 * `Intl` rather than a hand-rolled K/M/B ladder, and per market rather than per
 * browser: an Indian number groups by lakh and crore, and showing `1.8T` for a
 * figure an Indian user reads as `17.7 lakh crore` is a wrong-looking number
 * rather than a differently-formatted one.
 */
export function formatCap(value: number | null | undefined, market: MarketInfo): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "";
  let formatter = compactFormatters.get(market.code);
  if (!formatter) {
    formatter = new Intl.NumberFormat(market.locale, {
      notation: "compact",
      maximumFractionDigits: 2,
    });
    compactFormatters.set(market.code, formatter);
  }
  return formatter.format(value);
}

/** A price in its own market's conventions, unprefixed. */
export function formatPrice(value: number | null | undefined, market: MarketInfo): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "";
  let formatter = priceFormatters.get(market.code);
  if (!formatter) {
    formatter = new Intl.NumberFormat(market.locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    priceFormatters.set(market.code, formatter);
  }
  return formatter.format(value);
}

/**
 * A money amount with its market's symbol — `$1,234.00`, `₹1,23,456.00`.
 *
 * The three hardcoded `$` in the trading-plan views are the entire currency
 * surface in the app, and they are wrong the moment a plan names an NSE symbol.
 */
export function formatMoney(
  value: number | null | undefined,
  market: MarketInfo,
  { decimals = 2 }: { decimals?: number } = {},
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "";
  return new Intl.NumberFormat(market.locale, {
    style: "currency",
    currency: market.currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}
